import {QlBuilderViolation, QlBuilderViolationCode} from "../../shared/ql/builder/ql-builder-violation";

/**
 * German user texts per stable policy violation code (api-contract.md "Violation-Codes (stabil, FE
 * uebersetzt)"). Typed as a complete `Record` so a new backend code is a COMPILE error here, never
 * a silently untranslated message.
 *
 * Several codes are deliberately overloaded by the backend policy after the engine spikes (no new codes,
 * see the `UiQlWidgetDefinitionPolicy` Javadoc), so their texts name every case the code covers:
 * - `unknownJoin`: unknown join, an extended JOIN (only `+` attributes are offered, S2) and a sorting on an
 *   `onlyFirst` join (S5).
 * - `unknownField`: unknown field and an `onlyFirst` join without its back-reference FK `<fk>Id` (S5).
 * - `sideCarSiblingJoin`: a `+` node next to a side-car (simple pipe, S2) and a regular join after the
 *   side-car join (S7).
 * - `sideCarFilter`: a filter on the side-car and a sorting on the side-car JOIN (sorting works only as
 *   `<hostAs>.ext_<id>`, S6).
 * - `groupByInconsistent`: non-groupBy joins in a groupBy definition and HAVING (`functionFilters`).
 * - `tooManyExtendedJoins`: `MAX_EXTENDED_NODES` is 1 (S2).
 * - `objectGenericFieldInGroupBy`: every object suffix incl. `:id` is refused, only `:value` (S3).
 */
export const UI_QL_VIOLATION_TEXTS: Readonly<Record<QlBuilderViolationCode, string>> = {
    forbiddenParameters: 'Parameter (z. B. NO_AUTH_CHECK) sind in Widget-Abfragen nicht erlaubt.',
    forbiddenLogics: 'Logiken sind in Widget-Abfragen nicht erlaubt.',
    forbiddenVariables: 'Variablen sind in Widget-Abfragen nicht erlaubt – ausgenommen relative Datumswerte in Datumsfiltern.',
    forbiddenDependsOn: 'Abhängigkeiten zu anderen Abfragen (dependsOn) sind nicht erlaubt.',
    forbiddenAudit: 'Historien-Abfragen (audit) sind nicht erlaubt.',
    forbiddenPolymorphicJoin: 'Polymorphe Verknüpfungen (#objectId) sind nicht erlaubt.',
    joinParametersNotAllowed: 'Verknüpfungsparameter sind nur bei externen Zusatzfeldern erlaubt.',
    joinParameterKeyNotAllowed: 'Die Version der externen Zusatzfelder fehlt oder ist ungültig.',
    extendedJoinNotLeaf: 'Unter einem erweiterten Feld sind keine weiteren Verknüpfungen oder Felder möglich.',
    extendedJoinFilterOrSort: 'Erweiterte Felder können weder gefiltert noch sortiert werden.',
    extendedJoinInGroupBy: 'Erweiterte Felder sind in gruppierten Abfragen nicht möglich.',
    tooManyExtendedJoins: 'Höchstens ein erweitertes Feld je Definition.',
    sideCarFilter: 'Externe Zusatzfelder können nicht gefiltert werden; sortiert wird nur über die Spalte der Hauptentität.',
    sideCarChildJoin: 'Unter externen Zusatzfeldern sind keine weiteren Verknüpfungen möglich.',
    sideCarInGroupBy: 'Externe Zusatzfelder sind in gruppierten Abfragen nicht möglich.',
    sideCarSiblingJoin: 'Neben externen Zusatzfeldern sind keine erweiterten Felder möglich, und weitere Verknüpfungen müssen vor den Zusatzfeldern stehen.',
    unknownField: 'Ein Feld ist für diese Entität nicht bekannt oder fehlt (bei „nur erster Eintrag“ muss das Rückverweis-Feld ausgewählt sein).',
    unknownJoin: 'Eine Verknüpfung ist für diese Entität nicht bekannt oder so nicht möglich (erweiterte Verknüpfungen, Sortierung bei „nur erster Eintrag“).',
    structuralId: 'Die Definition enthält eine nicht erlaubte Objekt-ID.',
    joinDepthExceeded: 'Verknüpfungen sind höchstens 3 Ebenen tief möglich.',
    tooManyJoins: 'Höchstens 8 Verknüpfungen je Definition (inklusive erweiterter Felder und externer Zusatzfelder).',
    tooManyFields: 'Höchstens 30 Felder je Knoten.',
    groupByRequired: 'Diagramm und Kategorie brauchen eine gruppierte Abfrage.',
    groupByInconsistent: 'In gruppierten Abfragen müssen alle Verknüpfungen gruppiert sein; Filter auf Kennzahlen sind nicht möglich.',
    objectGenericFieldInGroupBy: 'Objekt-typisierte generische Felder lassen sich nicht gruppieren – nur „:value“ einfacher generischer Felder.',
    pagingMissing: 'Die Zeilenbegrenzung (Limit) fehlt.',
    pagingTooLarge: 'Das Limit ist zu groß (höchstens 100 Zeilen, gruppiert 500).',
    mappingInvalid: 'Die Feldzuordnung des Widgets ist unvollständig oder ungültig.',
    aliasImmutable: 'Der Alias eines gespeicherten Widgets kann nicht geändert werden.',
    probeFailed: 'Der Probelauf der Abfrage ist fehlgeschlagen.',
    aliasExists: 'Dieser Alias ist bereits vergeben.',
    // the backend's own wording (its `explanation` says the same) - ONE text for the user, not two
    assistTruncated: 'Die Antwort des Modells wurde abgeschnitten – bitte die Anfrage vereinfachen.',
};

/**
 * Codes whose fixed text names a FIELD/JOIN problem. When the backend reports one of them for the whole
 * document (`path` `$`, or no path) the field text is misleading - the assistant's validator uses them for
 * technical failures too (QA #2, "unknownField · $") - so the server message is shown instead.
 */
const UI_QL_VIOLATION_FIELD_CODES: ReadonlySet<string> = new Set(['unknownField', 'unknownJoin']);

/**
 * German text of one violation: the code's fixed text; the server message for an unknown code, and for a
 * field/join code reported without a field path (`$`).
 */
export function uiQlViolationText(violation: QlBuilderViolation): string {
    const code = violation?.code;
    const message = violation?.message?.trim();
    const path = violation?.path?.trim();

    if (code && UI_QL_VIOLATION_FIELD_CODES.has(code) && (!path || path === '$') && message) {
        return message;
    }

    return (code && Object.prototype.hasOwnProperty.call(UI_QL_VIOLATION_TEXTS, code))
        ? UI_QL_VIOLATION_TEXTS[code]
        : (message || 'Unbekannter Regelverstoß.');
}
